import axios from 'axios';
import { initFeatureFlags } from '@shoflo/polymer/helpers/Feature/Feature';
import { getEnvVar } from '@shoflo/polymer/utils/getEnvVar';
import {
  GET_USER_FROM_SESSION,
  GET_USER_TEAMS,
  GET_USER_EVENTS,
  GET_USER_SELF_SERVICE_FEATURE_TOGGLES,
  UPDATE_USER,
  UPDATE_USER_LATEST_ROLES,
  UPDATE_USER_PASSWORD,
  LOGOUT_USER,
  CREATE_PASS_RESET_TOKEN,
  RESEND_VERIFY_EMAIL
} from 'actions/Types';

import { showNotification, clearModal } from 'actions';

import { UPCOMING_EVENT_FILTERS } from 'constants/EventFilters';

/**
 * Get a user from the session
 *
 * @param {}
 */
export const getUserFromSession = () => {
  return dispatch => {
    const action = {
      type: GET_USER_FROM_SESSION,
      request: async apiClient => {
        const user = await apiClient.token.getUser();

        try {
          await initFeatureFlags(user, getEnvVar('REACT_APP_LAUNCHDARKLY_KEY'));
        } catch (e) {
          console.warn('Failed to load feature flag settings.');
        }

        return user;
      }
    };

    dispatch(action);
  };
};

/**
 * Get the teams for a users
 */
export const getUserTeams = () => {
  return (dispatch, getState) => {
    const { user } = getState();
    const userId = user._id;

    const action = {
      type: GET_USER_TEAMS,
      request: async apiClient => {
        const user = await apiClient.team.getByUserId(userId);

        return user;
      }
    };

    dispatch(action);
  };
};

/**
 * Get the events for a users
 */
export const getUserEvents = () => {
  return (dispatch, getState) => {
    const { user } = getState();
    const userId = user._id;

    const action = {
      type: GET_USER_EVENTS,
      request: async apiClient => {
        const user = await apiClient.event.getByUserId(userId, {
          size: 100,
          ...UPCOMING_EVENT_FILTERS
        });

        return user;
      }
    };

    dispatch(action);
  };
};

export const createPasswordResetToken = () => {
  return (dispatch, getState) => {
    const { user } = getState();
    const action = {
      type: CREATE_PASS_RESET_TOKEN,
      request: async apiClient => {
        await apiClient.user.createPasswordRecoveryToken(user.email);

        dispatch(showNotification(`Password reset email sent!`));

        return user;
      }
    };

    dispatch(action);
  };
};

/**
 * Update Latest User Event and Team Roles
 */
export const updateUserLatestRoles = (userId, changes) => {
  return (dispatch, getState) => {
    const { user: existingUserData } = getState();

    const action = {
      type: UPDATE_USER_LATEST_ROLES,
      request: async apiClient => {
        const user = await apiClient.user.update(userId, changes);

        return { ...existingUserData, ...user };
      }
    };

    dispatch(action);
  };
};

/**
 * Update User Information
 */
export const updateUser = (userId, payload) => {
  const { firstName, lastName, currentPassword, newPassword } = payload;

  return (dispatch, getState) => {
    const { user: existingUserData } = getState();

    const action = {
      type: UPDATE_USER,
      request: async apiClient => {
        let user;
        const response = await apiClient.user.update(userId, {
          firstName,
          lastName
        });
        user = { ...existingUserData, ...response };

        if (currentPassword && newPassword) {
          try {
            const userUpdatedPassword = await apiClient.user.updatePassword(
              userId,
              {
                currentPassword,
                newPassword
              }
            );
            user = userUpdatedPassword;
          } catch (e) {
            dispatch({
              type: `${UPDATE_USER_PASSWORD}_FAILURE`,
              payload: e.response
            });
          }
        }

        dispatch(clearModal('ACCOUNT_SETTINGS'));

        return user;
      }
    };

    dispatch(action);
  };
};

/**
 * Logout the pinche user
 */
export const logout = () => {
  return dispatch => {
    dispatch({
      type: LOGOUT_USER,
      request: async () => {
        const result = await axios.post('/logout');

        window.location.assign('/');

        return result;
      }
    });
  };
};

export const getSelfServiceFeatureToggles = () => {
  return dispatch => {
    const action = {
      type: GET_USER_SELF_SERVICE_FEATURE_TOGGLES,
      request: async apiClient => {
        const features = await apiClient.featureToggle.getSelfServiceForUsers();

        return features;
      }
    };

    dispatch(action);
  };
};

export const resendVerifyEmail = () => {
  return (dispatch, getState) => {
    const { user } = getState();

    const action = {
      type: RESEND_VERIFY_EMAIL,
      request: async apiClient => {
        await apiClient.user.resendVerifyEmail(user._id);

        return true;
      }
    };
    dispatch(action);
  };
};
